import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  monitoringPresentService,
  mapMonitoringPresentPage,
  type LC_ListMonitoringPresentIn,
  type LC_MonitoringFiltersIn,
  type MonitoringPresent,
} from '@/api/domains/attendance/monitoring';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
  PaginatedListSort,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMonitoringPresentResult {
  rows: MonitoringPresent[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  /** `true` desde que el backend responde por primera vez. */
  hasLoadedOnce: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  refetch: () => void;
}

const DEFAULT_SORT_KEY = 'fullName';

// Mismo orden por defecto en minúscula (lo emite Table) y mayúscula (lo espera el backend).
export const MONITORING_PRESENT_DEFAULT_SORT = {
  key: DEFAULT_SORT_KEY,
  direction: 'asc',
} as const;
const INITIAL_SORT: PaginatedListSort = { key: DEFAULT_SORT_KEY, direction: 'ASC' };

// Columnas cuyo key difiere del campo orderBy del backend (MonitoringPresentOrderByFields).
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
};

/**
 * Listado paginado "Presentes" del Monitoreo de Asistencia. Búsqueda, orden y paginación son
 * server-side vía `usePaginatedList`; el fetch espera el primer payload del panel de filtros.
 */
export function useMonitoringPresent(
  appliedFilters: LC_MonitoringFiltersIn | null,
  refreshToken = 0,
): UseMonitoringPresentResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<LC_MonitoringFiltersIn | null>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MonitoringPresent> | null> => {
      // Sin filtros no se consulta el endpoint: devolvería todo el holding.
      if (params.filters === null) {
        return { items: [], totalRecords: 0 };
      }

      const request: LC_ListMonitoringPresentIn = {
        ...params.filters,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      };

      const response = await monitoringPresentService.get(request, signal);

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('attendance:monitoring.present.loadError'));
          setHasLoadedOnce(true);
        }
        return null;
      }

      setHasLoadedOnce(true);
      return mapMonitoringPresentPage(response.data);
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters,
    refetch,
  } = usePaginatedList<MonitoringPresent, LC_MonitoringFiltersIn | null>({
    fetchFn,
    initialFilters: appliedFilters,
    initialSort: INITIAL_SORT,
    orderByMap: ORDER_BY_MAP,
    enabled: appliedFilters !== null,
  });

  useEffect(() => {
    setFilters(appliedFilters);
  }, [appliedFilters, setFilters]);

  // Reconsulta cuando cambia el token de refresco de la pantalla, conservando búsqueda, orden y
  // paginación; el token inicial no dispara un fetch extra.
  const previousRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (previousRefreshTokenRef.current === refreshToken) return;
    previousRefreshTokenRef.current = refreshToken;
    refetch();
  }, [refreshToken, refetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
    refetch,
  };
}

export default useMonitoringPresent;
